import { useState, useRef, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Scan, Package, Info, Play, CheckCircle2, XCircle, AlertTriangle,
  Clock, ChevronRight, Camera, Plus, Minus, Eye, EyeOff,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney, formatDateTime } from '@/lib/api'
import CameraScanner from '@/components/mobile/CameraScanner'

type Item = {
  id: number
  produto_id: number
  produto_codigo: string | null
  produto_descricao: string | null
  produto_ean: string | null
  produto_unidade: string | null
  quantidade_esperada: string | number
  quantidade_contada: string | number
  status: string
  divergencia: number | null
}

type Detalhe = {
  id: number
  codigo: string
  descricao: string | null
  deposito_nome: string | null
  status: string
  modo_cego: boolean
  total_itens: number
  total_contados: number
  total_divergentes: number
  valor_divergencia: string | number
  iniciado_em: string | null
  aprovado_em: string | null
  itens: Item[]
}

type Aba = 'bipar' | 'itens' | 'info'

const STATUS_CFG: Record<string, { label: string; cls: string }> = {
  rascunho:    { label: 'Rascunho',    cls: 'bg-ink-200 text-ink-700' },
  em_contagem: { label: 'Em contagem', cls: 'bg-blue-100 text-blue-700' },
  conferencia: { label: 'Conferência', cls: 'bg-amber-100 text-amber-700' },
  finalizado:  { label: 'Finalizado',  cls: 'bg-emerald-100 text-emerald-700' },
  cancelado:   { label: 'Cancelado',   cls: 'bg-red-100 text-red-700' },
}

const ITEM_CFG: Record<string, { label: string; cls: string }> = {
  pendente:   { label: 'Pendente',   cls: 'bg-ink-100 text-ink-600' },
  contado:    { label: 'Contado',    cls: 'bg-emerald-100 text-emerald-700' },
  divergente: { label: 'Divergente', cls: 'bg-amber-100 text-amber-700' },
  aprovado:   { label: 'Aprovado',   cls: 'bg-emerald-100 text-emerald-700' },
}

function num(v: string | number | null | undefined): number {
  if (v === null || v === undefined) return 0
  return typeof v === 'string' ? parseFloat(v) : v
}

function beep() {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain); gain.connect(ctx.destination)
    osc.frequency.value = 880
    gain.gain.setValueAtTime(0.2, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12)
    osc.start(); osc.stop(ctx.currentTime + 0.12)
  } catch {}
}

export default function InventarioDetalheMobilePage() {
  const { id } = useParams<{ id: string }>()
  const invId = Number(id)
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [aba, setAba] = useState<Aba>('bipar')
  const [scannerAberto, setScannerAberto] = useState(false)
  const [ultimaLeitura, setUltimaLeitura] = useState<{
    codigo: string
    descricao: string
    qtd: number
    tipo: 'sucesso' | 'erro'
    msg?: string
  } | null>(null)
  const [filtroItens, setFiltroItens] = useState<'todos' | 'pendente' | 'contado' | 'divergente'>('todos')

  const { data: inv, isLoading } = useQuery<Detalhe>({
    queryKey: ['inventario-m', invId],
    queryFn: async () => (await api.get(`/inventarios/${invId}`)).data,
    refetchInterval: (q) => {
      const d = q.state.data as Detalhe | undefined
      return d && ['em_contagem', 'conferencia'].includes(d.status) ? 15000 : false
    },
  })

  const bipar = useMutation({
    mutationFn: async (codigo: string) => {
      return (await api.post(`/inventarios/${invId}/bipar`, {
        codigo_ou_ean: codigo,
        quantidade: 1,
      })).data
    },
    onSuccess: (r) => {
      if (r.ok) {
        beep()
        setUltimaLeitura({
          codigo: r.produto_codigo,
          descricao: r.produto_descricao,
          qtd: num(r.quantidade_contada),
          tipo: 'sucesso',
        })
        qc.invalidateQueries({ queryKey: ['inventario-m', invId] })
      } else {
        try { navigator.vibrate?.([100, 60, 100]) } catch {}
        setUltimaLeitura({
          codigo: '',
          descricao: '',
          qtd: 0,
          tipo: 'erro',
          msg: r.mensagem,
        })
      }
    },
    onError: (e: any) => {
      try { navigator.vibrate?.([100, 60, 100]) } catch {}
      setUltimaLeitura({
        codigo: '',
        descricao: '',
        qtd: 0,
        tipo: 'erro',
        msg: e?.response?.data?.detail || 'Erro ao bipar',
      })
    },
  })

  const iniciar = useMutation({
    mutationFn: async () => (await api.post(`/inventarios/${invId}/iniciar`)).data,
    onSuccess: () => {
      toast.success('Iniciado')
      qc.invalidateQueries({ queryKey: ['inventario-m', invId] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const finalizarContagem = useMutation({
    mutationFn: async () => (await api.post(`/inventarios/${invId}/finalizar-contagem`)).data,
    onSuccess: () => {
      toast.success('Contagem finalizada')
      qc.invalidateQueries({ queryKey: ['inventario-m', invId] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const aprovar = useMutation({
    mutationFn: async () => (await api.post(`/inventarios/${invId}/aprovar`, { aprovar_todos: true })).data,
    onSuccess: () => {
      toast.success('Aprovado — estoque atualizado')
      qc.invalidateQueries({ queryKey: ['inventario-m', invId] })
      setAba('info')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  // Lista filtrada
  const itensFiltrados = (inv?.itens || []).filter(i => {
    if (filtroItens === 'todos') return true
    return i.status === filtroItens
  })

  if (isLoading || !inv) {
    return <div className="text-center text-ink-500 py-12"><Clock size={24} className="inline-block animate-pulse" /> Carregando...</div>
  }

  const stCfg = STATUS_CFG[inv.status] || STATUS_CFG.rascunho
  const emContagem = inv.status === 'em_contagem'
  const pct = inv.total_itens > 0 ? Math.round((inv.total_contados / inv.total_itens) * 100) : 0

  function handleScan(codigo: string) {
    setScannerAberto(false)  // fecha scanner
    bipar.mutate(codigo)      // dispara bipagem
    // reabre scanner apos 700ms pra proximo produto
    setTimeout(() => setScannerAberto(true), 700)
  }

  return (
    <div className="pb-20 space-y-3">
      {/* Header compacto do inventario */}
      <div className="bg-white rounded-lg p-3 shadow-sm border border-ink-200 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono font-semibold">{inv.codigo}</span>
            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${stCfg.cls}`}>{stCfg.label}</span>
            {inv.modo_cego && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 flex items-center gap-1">
                <EyeOff size={10} /> Cego
              </span>
            )}
          </div>
          <div className="text-xs text-ink-500 mt-0.5">{inv.deposito_nome}</div>
        </div>
        <div className="text-right">
          <div className="text-xl font-bold leading-none">{inv.total_contados}<span className="text-ink-400 text-base">/{inv.total_itens}</span></div>
          <div className="text-xs text-ink-500">{pct}%</div>
        </div>
      </div>
      <div className="h-1.5 bg-ink-200 rounded-full overflow-hidden">
        <div className="h-full bg-brand-500 transition-all" style={{ width: `${pct}%` }} />
      </div>

      {/* =========== CONTEUDO DA ABA =========== */}
      {aba === 'bipar' && (
        <div className="space-y-3">
          {inv.status === 'rascunho' && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm text-amber-900">
              Inventário em rascunho.
              <button
                onClick={() => iniciar.mutate()}
                className="btn btn-primary w-full mt-2"
              >
                <Play size={16} /> Iniciar contagem
              </button>
            </div>
          )}

          {emContagem && (
            <>
              <button
                onClick={() => setScannerAberto(true)}
                className="w-full bg-brand-600 active:bg-brand-700 text-white py-8 rounded-xl shadow-lg flex flex-col items-center gap-2"
              >
                <Camera size={40} />
                <span className="font-bold text-lg">Abrir câmera</span>
                <span className="text-xs text-white/80">ou toque pra usar teclado</span>
              </button>

              {/* Ultima leitura */}
              {ultimaLeitura && ultimaLeitura.tipo === 'sucesso' && (
                <div className="bg-emerald-50 border border-emerald-300 rounded-lg p-3 animate-pulse-once">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 size={20} className="text-emerald-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-mono font-semibold text-emerald-900">{ultimaLeitura.codigo}</div>
                      <div className="text-xs text-ink-600 truncate">{ultimaLeitura.descricao}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-emerald-700 leading-none">{ultimaLeitura.qtd}</div>
                      <div className="text-xs text-ink-500">total</div>
                    </div>
                  </div>
                </div>
              )}
              {ultimaLeitura && ultimaLeitura.tipo === 'erro' && (
                <div className="bg-red-50 border border-red-300 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <XCircle size={20} className="text-red-600" />
                    <div className="text-sm text-red-900 flex-1">{ultimaLeitura.msg}</div>
                  </div>
                </div>
              )}
            </>
          )}

          {inv.status === 'conferencia' && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm text-amber-900">
              Contagem finalizada.{' '}
              <button onClick={() => setAba('info')} className="underline font-medium">
                Ir pra aprovação
              </button>
            </div>
          )}

          {['finalizado', 'aprovado', 'cancelado'].includes(inv.status) && (
            <div className="text-center text-ink-500 py-8">
              <CheckCircle2 size={32} className="inline-block text-emerald-500 mb-2" />
              <div>Inventário {stCfg.label.toLowerCase()}.</div>
            </div>
          )}
        </div>
      )}

      {aba === 'itens' && (
        <div className="space-y-3">
          {/* Chips de filtro */}
          <div className="flex gap-2 overflow-x-auto pb-1">
            {(['todos', 'pendente', 'contado', 'divergente'] as const).map(f => {
              const count = f === 'todos'
                ? inv.itens.length
                : inv.itens.filter(i => i.status === f).length
              return (
                <button
                  key={f}
                  onClick={() => setFiltroItens(f)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${
                    filtroItens === f
                      ? 'bg-brand-600 text-white'
                      : 'bg-white border border-ink-300 text-ink-700'
                  }`}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)} ({count})
                </button>
              )
            })}
          </div>

          {itensFiltrados.length === 0 ? (
            <div className="text-center text-ink-500 py-8 text-sm">Nenhum item.</div>
          ) : (
            itensFiltrados.map(it => {
              const cfg = ITEM_CFG[it.status] || ITEM_CFG.pendente
              const diff = num(it.quantidade_contada) - num(it.quantidade_esperada)
              return (
                <div key={it.id} className="bg-white rounded-lg p-3 shadow-sm border border-ink-200">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs font-semibold">{it.produto_codigo}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${cfg.cls}`}>{cfg.label}</span>
                      </div>
                      <div className="text-sm text-ink-800 truncate mt-0.5">{it.produto_descricao}</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-lg font-bold leading-none">{num(it.quantidade_contada)}</div>
                      {!inv.modo_cego && (
                        <div className="text-xs text-ink-500">esperado {num(it.quantidade_esperada)}</div>
                      )}
                      {!inv.modo_cego && diff !== 0 && (
                        <div className={`text-xs font-semibold ${diff > 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                          {diff > 0 ? '+' : ''}{diff}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>
      )}

      {aba === 'info' && (
        <div className="space-y-3">
          {/* KPIs */}
          <div className="grid grid-cols-2 gap-2">
            <KpiCard label="Itens contados" value={`${inv.total_contados}/${inv.total_itens}`} />
            <KpiCard label="Divergentes" value={inv.total_divergentes} destaque={inv.total_divergentes > 0 ? 'amber' : undefined} />
            <KpiCard label="Valor divergência" value={formatMoney(num(inv.valor_divergencia))} destaque="amber" />
            <KpiCard label="Progresso" value={`${pct}%`} />
          </div>

          {/* Acoes de status */}
          {emContagem && (
            <button
              onClick={() => { if (confirm('Finalizar contagem? Depois ainda pode ajustar antes de aprovar.')) finalizarContagem.mutate() }}
              className="w-full btn btn-primary py-3"
            >
              Finalizar contagem
            </button>
          )}

          {inv.status === 'conferencia' && (
            <button
              onClick={() => { if (confirm('Aplicar ajustes no estoque?')) aprovar.mutate() }}
              className="w-full py-3 rounded-lg bg-emerald-600 active:bg-emerald-700 text-white font-bold text-lg shadow-lg flex items-center justify-center gap-2"
            >
              <CheckCircle2 size={20} /> Aprovar e aplicar
            </button>
          )}

          {/* Dados */}
          <div className="bg-white rounded-lg p-3 shadow-sm border border-ink-200 text-sm space-y-1.5">
            <Linha label="Código" value={inv.codigo} mono />
            <Linha label="Depósito" value={inv.deposito_nome || '—'} />
            <Linha label="Status" value={stCfg.label} />
            {inv.iniciado_em && <Linha label="Iniciado em" value={formatDateTime(inv.iniciado_em)} />}
            {inv.aprovado_em && <Linha label="Aprovado em" value={formatDateTime(inv.aprovado_em)} />}
          </div>

          <button
            onClick={() => navigate(`/estoque/inventarios/${invId}`)}
            className="w-full text-xs text-ink-500 py-2 underline"
          >
            Abrir na versão desktop
          </button>
        </div>
      )}

      {/* =========== TAB BAR FIXA =========== */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-ink-200 flex items-stretch z-10 shadow-[0_-2px_8px_rgba(0,0,0,0.05)]">
        <TabButton active={aba === 'bipar'}  onClick={() => setAba('bipar')}  icon={Scan} label="Bipar" />
        <TabButton active={aba === 'itens'}  onClick={() => setAba('itens')}  icon={Package} label={`Itens (${inv.total_contados}/${inv.total_itens})`} />
        <TabButton active={aba === 'info'}   onClick={() => setAba('info')}   icon={Info} label="Info" />
      </div>

      {/* Scanner overlay */}
      {scannerAberto && (
        <CameraScanner
          onScan={handleScan}
          onClose={() => setScannerAberto(false)}
        />
      )}
    </div>
  )
}

function TabButton({ active, onClick, icon: Icon, label }: any) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 py-2.5 flex flex-col items-center gap-0.5 text-xs font-medium ${
        active ? 'text-brand-600' : 'text-ink-500'
      } active:bg-ink-50`}
    >
      <Icon size={20} />
      <span className="text-[10px]">{label}</span>
    </button>
  )
}

function KpiCard({ label, value, destaque }: { label: string; value: any; destaque?: 'amber' | 'emerald' }) {
  const color = destaque === 'amber' ? 'text-amber-700' : destaque === 'emerald' ? 'text-emerald-700' : 'text-ink-900'
  return (
    <div className="bg-white rounded-lg p-3 shadow-sm border border-ink-200">
      <div className="text-xs text-ink-500">{label}</div>
      <div className={`text-lg font-bold ${color}`}>{value}</div>
    </div>
  )
}

function Linha({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-ink-500 text-xs">{label}</span>
      <span className={`font-medium ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  )
}
